"use client";

import React, { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { MenuProvider } from "@/integrations/wordpress/WordPressMenuProvider";
function DesktopMenuConsumer() {
  const navItems = [{
    label: "How It Works",
    href: "#how-it-works"
  }, {
    label: "Scientific Methodology",
    href: "#scientific-methodology"
  }, {
    label: "Pricing",
    href: "#schools"
  }];
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-8">
        {navItems.map((item, i) => <NavigationMenuItem key={item.label} data-index={i}>
            <NavigationMenuLink asChild className="!bg-transparent hover:!bg-transparent focus:!bg-transparent data-[active=true]:!bg-transparent px-0 py-2">
              <Link to={item.href} className="group relative text-[13px] font-default uppercase tracking-widest text-foreground transition-colors hover:text-primary">
                {item.label}
                <span className="absolute -bottom-1 left-0 h-[1px] w-full origin-right scale-x-0 bg-primary transition-transform duration-300 ease-out group-hover:origin-left group-hover:scale-x-100 shadow-[0_0_8px_rgba(201,184,245,0.5)]" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer() {
  const navItems = [{
    label: "How It Works",
    href: "#how-it-works"
  }, {
    label: "Scientific Methodology",
    href: "#scientific-methodology"
  }, {
    label: "Pricing",
    href: "#schools"
  }];
  return <nav className="flex flex-col gap-6 mt-12">
      {navItems.map((item, i) => <div key={item.label} data-index={i}>
          <Link to={item.href} className="text-xl font-default uppercase tracking-widest text-foreground hover:text-primary transition-colors block py-2">
            {item.label}
          </Link>
        </div>)}
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  const scrollToSection = (id: string) => {
    const element = document.querySelector(id);
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
    }
  };
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            const headerOffset = 80;
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
            window.scrollTo({
              top: offsetPosition,
              behavior: "smooth"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="221622" id="header" className={`sticky top-0 z-50 w-full transition-all duration-500 ${isScrolled ? "bg-[#09080A]/80 backdrop-blur-xl border-b border-primary/30 shadow-lg" : "bg-[#09080A] border-b border-primary/20"}`}>
      <div className="container mx-auto px-4 md:px-8 max-w-7xl h-20 flex items-center justify-between">
        <div className="flex items-center shrink-0">
          <Link to="/">
            <span className="text-[#C48A3A] font-serif text-2xl font-bold tracking-[0.15em]">
              TERRIGMA
            </span>
          </Link>
        </div>

        <MenuProvider menu_id="17">
          <DesktopMenuConsumer />
        </MenuProvider>

        <div className="flex items-center gap-4">
          <div className="hidden md:block">
            <Button onClick={() => scrollToSection("#be-the-first-to-dig-in")} className="bg-[#7C5CBF] hover:bg-[#7C5CBF]/90 text-white font-default text-xs uppercase tracking-widest rounded-sm px-6 py-2 transition-all duration-300 shadow-md hover:shadow-[#7C5CBF]/20">JOIN THE WAITLIST</Button>
          </div>
          
          <div className="md:hidden flex items-center">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Menü öffnen" className="text-foreground hover:bg-accent/20">
                  <Menu className="size-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[400px] bg-background border-l border-primary/20 p-6">
                <div className="flex flex-col h-full">
                  <div className="mt-2">
                    <Link to="/">
                      <span className="text-[#C48A3A] font-serif text-xl font-bold tracking-[0.15em]">
                        TERRIGMA
                      </span>
                    </Link>
                  </div>
                  <MenuProvider menu_id="17">
                    <MobileMenuConsumer />
                  </MenuProvider>
                  <div className="mt-auto pb-8">
                    <Button onClick={() => scrollToSection("#be-the-first-to-dig-in")} className="w-full bg-[#7C5CBF] hover:bg-[#7C5CBF]/90 text-white font-default text-xs uppercase tracking-widest rounded-sm py-4">
                      Early Access
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </section>;
}